import { CircularProgress, makeStyles, Theme } from '@material-ui/core';
import MuiCreateIcon from '@material-ui/icons/Create';
import classNames from 'classnames';
import React, { useState } from 'react';

import { CardType, FullAnswerFragment, QuestionDetailsFragment } from '../../../../graphql/gen/types';
import { colorPalette, spacings } from '../../../../theme';
import { ButtonColor } from '../../../shared/primitives/PlexButton';
import { IconButtonSize, PlexIconButton } from '../../../shared/primitives/PlexIconButton';
import { PostAuthor } from '../../common/PostAuthor';
import { PostDate } from '../../common/PostDate';
import { useDialogState } from '../../dialog/useDialogState';
import { AnswerViewContent } from '../../feed/QuestionView/AnswerViewContent';
import { canEditAnswer } from '../../permissions';
import { MoreAnswerButton } from '../answer/MoreAnswerButton';
import { UpVoteAnswerButton } from '../answer/UpVoteAnswerButton';

type Props = {
  className?: string;
  question: QuestionDetailsFragment;
  answer: FullAnswerFragment;
};

export function QuestionAnswer({ className, answer, question }: Props): React.ReactElement {
  const classes = useStyles();
  const [, setDialogState] = useDialogState();
  const [loading, setLoading] = useState(false);

  const plexRelation = answer.question.plex.currentUserRelation.state;
  const answerRelation = answer.currentUserRelation;

  const handleEdit = (): void => {
    setDialogState({ type: 'edit_fact_answer', factAnswerId: answer.id });
  };

  const headerView = (
    <div className={classes.header}>
      <PostAuthor authorName={answer.user.name} authorTitle={answer.user.jobTitle} authorAvatar={answer.user.picture} />
      <PostDate prefix="" date={new Date(answer.publishedAt)} />
    </div>
  );

  const footerView = (
    <div className={classNames(classes.answerActionButtons)}>
      <div>
        {question.type === CardType.Faq && (
          <UpVoteAnswerButton answer={answer} disabled={loading} onLoadingChanged={setLoading} />
        )}
      </div>
      <div className={classes.actionsRightContainer}>
        {loading && <CircularProgress size={24} />}

        {canEditAnswer({ plexRelation, answerRelation }) && (
          <PlexIconButton
            onClick={handleEdit}
            MuiIcon={MuiCreateIcon}
            color={ButtonColor.TransparentGray}
            tooltip={'Edit'}
            size={IconButtonSize.Normal}
          />
        )}
        <MoreAnswerButton answer={answer} loading={loading} onLoadingChanged={setLoading} question={question} />
      </div>
    </div>
  );

  return (
    <AnswerViewContent
      className={classNames(classes.answerRoot, className)}
      question={question}
      answer={answer}
      selected={false}
      showAllLines={true}
      headerView={headerView}
      footerView={footerView}
    />
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  answerRoot: {
    marginLeft: spacings.xxlarge,
    marginRight: spacings.xxlarge,
  },
  answerActionButtons: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopStyle: 'solid',
    padding: spacings.small,
    borderTopColor: colorPalette.purpleGray._100,
  },
  actionsRightContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacings.normal,
    borderBottom: `1px solid ${colorPalette.purpleGray._100}`,
  },
}));
